import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useTwilioVoice } from "@/context/TwilioVoiceContext";
import { PhoneCall, X, Search, Clock } from "lucide-react";
import { STATUS_LIST, STATUS_LABELS } from "@/components/StatusBadge";

/**
 * Auto-opens when a Twilio call ends (duration >= 3s).
 * If contactId is known (call from CRM), we pre-select it.
 * Otherwise, the rep searches/picks a contact (or "Σκιπ").
 */
export default function PostCallModal() {
  const { lastCall, clearLastCall } = useTwilioVoice();
  const { lang } = useI18n();
  const L = lang === "el";

  const [contact, setContact] = useState(null);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [status, setStatus] = useState("called");
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [nextAt, setNextAt] = useState("");
  const [saving, setSaving] = useState(false);

  // When a new call ends, reset state and try to fetch contact if id known
  useEffect(() => {
    if (!lastCall) return;
    setContact(null);
    setSearch("");
    setResults([]);
    setStatus("called");
    setReason("");
    setNote("");
    setNextAt("");
    if (lastCall.contactId) {
      api.get(`/contacts/${lastCall.contactId}`).then((r) => setContact(r.data)).catch(() => {});
    } else if (lastCall.phone) {
      // attempt to find by phone match
      const digits = lastCall.phone.replace(/\D/g, "").slice(-7);
      api.get(`/contacts?search=${encodeURIComponent(digits)}`)
        .then((r) => {
          const hit = (r.data || []).find((c) => (c.phone || "").replace(/\D/g, "").endsWith(digits));
          if (hit) setContact(hit);
        }).catch(() => {});
    }
  }, [lastCall]);

  // Search for contacts
  useEffect(() => {
    if (contact || !search.trim()) { setResults([]); return; }
    const id = setTimeout(() => {
      api.get(`/contacts?search=${encodeURIComponent(search)}`)
        .then((r) => setResults((r.data || []).slice(0, 8)))
        .catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(id);
  }, [search, contact]);

  if (!lastCall) return null;

  const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
  const needsNextDate = ["callback_scheduled", "meeting_scheduled"].includes(status);
  const needsReason = ["not_interested", "lost", "won"].includes(status);

  const submit = async (e) => {
    e?.preventDefault();
    if (!contact) return;
    setSaving(true);
    try {
      await api.post(`/contacts/${contact.id}/status`, {
        pipeline_status: status,
        status_reason: reason || null,
        note: note || `Κλήση ${fmt(lastCall.durationSec)} · ${lastCall.phone}`,
        next_action_at: nextAt ? new Date(nextAt).toISOString() : null,
      });
      clearLastCall();
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 z-[60] flex items-center justify-center p-4" data-testid="post-call-modal">
      <div className="bg-white rounded-xl max-w-lg w-full shadow-2xl overflow-hidden">
        <header className="px-6 py-4 bg-slate-900 text-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
              <PhoneCall className="w-5 h-5 text-emerald-300" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-slate-300 font-semibold">{L ? "Κλήση ολοκληρώθηκε" : "Call ended"}</div>
              <div className="font-semibold">
                {lastCall.phone}
                <span className="ml-2 text-xs text-slate-300 font-mono">
                  <Clock className="inline w-3 h-3 mr-1" /> {fmt(lastCall.durationSec)}
                </span>
              </div>
            </div>
          </div>
          <button data-testid="post-call-skip" onClick={clearLastCall} className="p-1.5 rounded hover:bg-white/10" title={L ? "Παράλειψη" : "Skip"}>
            <X className="w-5 h-5" />
          </button>
        </header>

        <div className="px-6 py-5 space-y-4">
          <h3 className="font-heading text-lg font-semibold text-slate-900">
            {L ? "Πώς πήγε; Κατάγραψε το επόμενο βήμα" : "How did it go? Log next step"}
          </h3>

          {/* Contact selector */}
          {contact ? (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 flex items-center justify-between" data-testid="post-call-contact">
              <div className="min-w-0">
                <div className="font-semibold text-slate-900 truncate">{contact.company_name || `${contact.first_name} ${contact.last_name}`}</div>
                <div className="text-xs text-slate-600 truncate">{contact.first_name} {contact.last_name} · {contact.phone || contact.email}</div>
              </div>
              <button onClick={() => setContact(null)} className="text-xs text-slate-500 hover:text-slate-900 font-semibold">{L ? "Αλλαγή" : "Change"}</button>
            </div>
          ) : (
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-700">{L ? "Με ποιον μίλησες;" : "Who did you call?"}</label>
              <div className="mt-1.5 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  data-testid="post-call-search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={L ? "Όνομα εταιρείας ή επαφής..." : "Company or contact name..."}
                  className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0052CC]/40 focus:border-[#0052CC]"
                  autoFocus
                />
              </div>
              {results.length > 0 && (
                <ul className="mt-1.5 border border-slate-200 rounded-md divide-y divide-slate-100 max-h-44 overflow-y-auto bg-white" data-testid="post-call-results">
                  {results.map((r) => (
                    <li key={r.id}>
                      <button
                        data-testid={`post-call-pick-${r.id}`}
                        onClick={() => { setContact(r); setSearch(""); setResults([]); }}
                        className="w-full text-left px-3 py-2 hover:bg-slate-50"
                      >
                        <div className="text-sm font-semibold text-slate-900">{r.company_name || `${r.first_name} ${r.last_name}`}</div>
                        <div className="text-xs text-slate-500">{r.first_name} {r.last_name} · {r.phone || r.email || "—"}</div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* Status selector */}
          {contact && (
            <form onSubmit={submit} className="space-y-3" data-testid="post-call-form">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-700">{L ? "Αποτέλεσμα" : "Outcome"}</label>
                <div className="mt-1.5 grid grid-cols-2 gap-2">
                  {STATUS_LIST.filter((s) => s !== "new").map((s) => (
                    <button
                      type="button"
                      key={s}
                      data-testid={`post-call-status-${s}`}
                      onClick={() => setStatus(s)}
                      className={`px-3 py-2 rounded-md text-sm font-semibold border transition-colors text-left ${
                        status === s
                          ? "border-[#0052CC] bg-[#0052CC]/5 text-[#0052CC]"
                          : "border-slate-200 text-slate-700 hover:bg-slate-50"
                      }`}
                    >
                      {L ? STATUS_LABELS[s].el : STATUS_LABELS[s].en}
                    </button>
                  ))}
                </div>
              </div>

              {needsNextDate && (
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-700">
                    {status === "callback_scheduled" ? (L ? "Πότε να ξανακαλέσω;" : "When to call back?") : (L ? "Ημερομηνία ραντεβού" : "Meeting date")}
                  </label>
                  <input
                    data-testid="post-call-next-at"
                    type="datetime-local"
                    required
                    value={nextAt}
                    onChange={(e) => setNextAt(e.target.value)}
                    className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-md text-sm"
                  />
                </div>
              )}

              {needsReason && (
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-700">
                    {L ? "Λόγος *" : "Reason *"}
                  </label>
                  <input
                    data-testid="post-call-reason"
                    type="text"
                    required
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder={L ? "π.χ. Δουλεύει με άλλο προμηθευτή" : "e.g. Works with another supplier"}
                    className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-md text-sm"
                  />
                </div>
              )}

              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-700">{L ? "Σημείωση (προαιρετική)" : "Note (optional)"}</label>
                <textarea
                  data-testid="post-call-note"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={2}
                  placeholder={L ? "Λεπτομέρειες συζήτησης..." : "Conversation details..."}
                  className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-md text-sm"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                <button type="button" onClick={clearLastCall} data-testid="post-call-cancel" className="px-4 py-2 border border-slate-200 rounded-md text-sm font-medium">
                  {L ? "Παράλειψη" : "Skip"}
                </button>
                <button type="submit" data-testid="post-call-submit" disabled={saving} className="px-4 py-2 bg-[#0052CC] text-white rounded-md text-sm font-semibold disabled:opacity-50">
                  {saving ? "..." : (L ? "Αποθήκευση" : "Save")}
                </button>
              </div>
            </form>
          )}

          {!contact && (
            <div className="text-xs text-slate-500 pt-1">
              {L
                ? "Αν η επαφή δεν είναι αποθηκευμένη, πάτησε 'Παράλειψη' και πρόσθεσέ τη μετά από τις Επαφές."
                : "If the contact isn't saved, click 'Skip' and add them later in Customers."}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
